import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  mapAssistancesToCapitalGlobalAssistanceCatalog,
  mapAssistancesToSelectOptions,
  mapCapitalizationsToSelectOptions,
  mapCoveragesToCapitalGlobalCoverageCatalog,
} from '@/features/quotes/schemas/capital-global/map-capital-global-catalog-from-api';
import { coveragesService } from '@/services/coverages';
import type { CoverageUmbrella } from '@/services/coverages/types';

function getTodayAsVersionDate(): string {
  return new Date().toISOString().split('T')[0];
}

export function useCapitalGlobalCoveragesCatalog() {
  const versionDate = getTodayAsVersionDate();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['capital-global-catalog', versionDate],
    queryFn: async () => {
      const result = await coveragesService.getGlobalCoverages(versionDate);
      if (!result.success || !result.data) {
        throw new Error(
          result.error ?? 'Falha ao buscar catálogo de coberturas'
        );
      }
      return result.data;
    },
  });

  const assistanceUmbrella: CoverageUmbrella | null = data?.assistance ?? null;
  const capitalizationUmbrella: CoverageUmbrella | null =
    data?.capitalization ?? null;

  const coverageCatalog = useMemo(
    () =>
      data ? mapCoveragesToCapitalGlobalCoverageCatalog(data.coverages) : [],
    [data]
  );

  const assistanceCatalog = useMemo(
    () =>
      assistanceUmbrella
        ? mapAssistancesToCapitalGlobalAssistanceCatalog(
            assistanceUmbrella.benefits
          )
        : [],
    [assistanceUmbrella]
  );

  const assistanceFuneralOptions = useMemo(
    () =>
      assistanceUmbrella
        ? mapAssistancesToSelectOptions(assistanceUmbrella.benefits)
        : [],
    [assistanceUmbrella]
  );

  const capitalizationOptions = useMemo(
    () =>
      capitalizationUmbrella
        ? mapCapitalizationsToSelectOptions(capitalizationUmbrella.benefits)
        : [],
    [capitalizationUmbrella]
  );

  return {
    coverageCatalog,
    assistanceCatalog,
    assistanceFuneralOptions,
    capitalizationOptions,
    assistanceUmbrella,
    capitalizationUmbrella,
    isLoading,
    isError,
    error,
    refetch,
  };
}
